import { useCallback } from "react";
import ChartCard from "../../components/ChartCard";
import StatTile from "../../components/StatTile";
import StackedColumns, { EmptyChartNote } from "../../components/charts/StackedColumns";
import { CardGrid, ErrorBanner, FactList, Section } from "../../components/Layout";
import { TagChip } from "../../components/Pills";
import { useDashboardData } from "../../hooks/useDashboardData";
import { fetchCustomerStats } from "../../dashboardApi";
import { formatCents, formatCentsCompact, formatDate, formatNumber } from "../../format";
import { viz } from "../../styles";
import { CustomerDetail, CustomerStatsResponse } from "../../types";
import { useIsNetBasis } from "../../revenueBasis";

const SERIES = [
  { key: "subscription", label: "Subscription", color: viz.series1 },
  { key: "ticketing", label: "Ticketing fees", color: viz.series2 },
  { key: "other", label: "Other fees", color: viz.series4 },
];

/** "2026-09" → "Sep 2026". */
const monthLabel = (month: string): string => {
  const date = new Date(`${month}-01T00:00:00.000Z`);
  if (isNaN(date.getTime())) return month;
  return date.toLocaleDateString("en-US", {
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
};

/**
 * What Markit earns from this customer, from the nightly rollup, plus the live
 * subscription facts underneath it.
 *
 * The two are on different clocks and say so: revenue carries a "cached" chip
 * with the sync time, the subscription facts carry "live".
 */
const RevenueSection = ({ customer }: { customer: CustomerDetail }) => {
  const { subscription } = customer;
  const isNet = useIsNetBasis();
  const overflowUsed =
    subscription.messageLimit !== null
      ? `${formatNumber(subscription.messagesUsed)} of ${formatNumber(subscription.messageLimit)}`
      : formatNumber(subscription.messagesUsed);

  const fetcher = useCallback(() => fetchCustomerStats(customer.uid), [customer.uid]);
  const { data, error, refreshing } = useDashboardData<CustomerStatsResponse>(
    fetcher,
    [customer.uid]
  );

  const stats = data?.stats;
  const syncedAt = data?.sync.lastRunFinishedAt ?? undefined;
  const freshness = { kind: "cached" as const, updatedAt: syncedAt };

  // Every series is what Markit actually kept. Subscription arrives already net
  // of refunds from the rollup (netted at source in stripeSync), so it must not
  // be adjusted again here; the ticketing-fee refund is still gross and comes
  // off the fee line it belongs to.
  //
  // On the net basis each series additionally loses its OWN stream's Stripe
  // fee, so the three bars still sum to the row's net total. A month predating
  // the Sep 2026 fee capture reports 0 fee, which is why `monthsWithFee` below
  // states the chart's own coverage rather than letting those months quietly
  // read as fee-free.
  const months = (data?.months ?? []).map(month => ({
    month: month.month,
    subscription:
      month.subscriptionRevenueCents +
      month.overflowRevenueCents -
      (isNet ? month.subscriptionStripeFeeCents ?? 0 : 0),
    ticketing:
      month.ticketingFeeRevenueCents - (isNet ? month.ticketingStripeFeeCents ?? 0 : 0),
    other:
      month.otherFeeRevenueCents -
      month.refundedFeeCents -
      (isNet ? month.otherStripeFeeCents ?? 0 : 0),
  }));

  /** Months that carry any Stripe fee at all — the net chart's own coverage. */
  const monthsWithFee = (data?.months ?? []).filter(
    month =>
      (month.subscriptionStripeFeeCents ?? 0) +
        (month.ticketingStripeFeeCents ?? 0) +
        (month.otherStripeFeeCents ?? 0) >
      0
  ).length;

  // Distinguishes "the rollup has never seen this customer" from "it has, and
  // they've paid nothing" — the first shows a dash, the second shows $0.
  const noRollupYet = !stats;

  // Optional on the wire: documents written before subscription-refund netting
  // shipped don't carry it, and those are genuinely "zero refunds recorded".
  const subscriptionRefunded = stats?.lifetime.subscriptionRefundCents ?? 0;
  const ticketingFeesRefunded = stats?.lifetime.refundedFeeCents ?? 0;

  // Stripe's own cut, a third kind of thing again from the two refunds above:
  // a refund is money Markit took and gave back, this is money that never
  // reached Markit. Absent on documents written before Sep 2026, and absent
  // is not zero — `hasFee` is what stops a net tile printing a gross figure.
  const lifetimeStripeFee = stats?.lifetime.stripeFeeCents;
  const hasFee = lifetimeStripeFee !== undefined && lifetimeStripeFee > 0;
  /**
   * A net figure, or null when the fee behind it was never measured.
   *
   * Null renders "—" with a reason rather than falling back to the gross
   * number, which under a net label would claim Markit paid Stripe nothing —
   * the "— is not zero" rule this dashboard runs on, applied to a figure that
   * would otherwise look perfectly plausible.
   */
  const netOr = (grossCents: number, feeCents: number | undefined) =>
    !isNet
      ? grossCents
      : feeCents === undefined || !hasFee
      ? null
      : grossCents - feeCents;

  const showCents = (value: number | null) =>
    value === null ? "—" : formatCents(value);

  return (
    <Section title="Revenue for Markit">
      {error && <ErrorBanner message={error} />}

      <CardGrid min={220}>
        <StatTile
          label={`Lifetime Markit revenue${isNet ? ", net" : ""}`}
          value={
            noRollupYet
              ? "—"
              : showCents(netOr(stats!.lifetime.totalRevenueCents, lifetimeStripeFee))
          }
          secondary={
            noRollupYet
              ? "Not yet recorded by the nightly rollup"
              : isNet && !hasFee
              ? "Stripe's cut was never recorded for this customer — backfill pending"
              : isNet
              ? `After ${formatCents(lifetimeStripeFee!)} of Stripe fees`
              : stats!.firstRevenueAt
              ? `First revenue ${formatDate(stats!.firstRevenueAt)}`
              : "No revenue recorded"
          }
          title={
            isNet && !hasFee
              ? "This customer's charges predate the Sep 2026 Stripe-fee capture, so their fee was never measured. Showing the gross figure under a net label would claim Markit paid Stripe nothing."
              : undefined
          }
          freshness={freshness}
        />
        <StatTile
          label={`Subscription revenue${isNet ? ", net" : ""}`}
          value={
            noRollupYet
              ? "—"
              : showCents(
                  netOr(
                    stats!.lifetime.subscriptionRevenueCents +
                      stats!.lifetime.overflowRevenueCents,
                    (stats!.lifetime.subscriptionStripeFeeCents ?? 0) +
                      (stats!.lifetime.overflowStripeFeeCents ?? 0)
                  )
                )
          }
          secondary={
            isNet
              ? "Lifetime · baseline plus overflow, net of refunds and Stripe fees"
              : "Lifetime · baseline plus overflow, net of refunds"
          }
          freshness={freshness}
        />
        <StatTile
          label={`Ticketing fee revenue${isNet ? ", net" : ""}`}
          value={
            noRollupYet
              ? "—"
              : showCents(
                  netOr(
                    stats!.lifetime.ticketingFeeRevenueCents,
                    stats!.lifetime.ticketingStripeFeeCents
                  )
                )
          }
          secondary={(() => {
            if (noRollupYet) return "Not yet recorded";
            // The trailing figure follows the SAME basis as the value above
            // it. A net headline over a gross "last 30d" is two bases on one
            // tile with nothing saying so — the quieter version of the
            // mismatch this whole toggle is global to avoid.
            const trailing = showCents(
              netOr(
                stats!.trailing30d.ticketingFeeRevenueCents,
                stats!.trailing30d.ticketingStripeFeeCents
              )
            );
            return ticketingFeesRefunded > 0
              ? `Last 30d ${trailing} · ${formatCents(
                  ticketingFeesRefunded
                )} of fees refunded`
              : `Last 30d ${trailing}`;
          })()}
          title={
            ticketingFeesRefunded > 0
              ? "Markit's own fee handed back. A creator refunding a ticket normally absorbs it themselves and Markit keeps its fee, so this is rare."
              : undefined
          }
          freshness={freshness}
        />
        {/*
          Subscription refunds only. This is deliberately NOT the old combined
          "Refunded fees" tile: a refunded ticket costs Markit nothing (the
          creator absorbs it, Markit keeps its fee), a refunded subscription is
          Markit's own money going back out. Showing one number for both would
          say "we lost this" about revenue that never left. The rare ticketing
          case now rides on the ticketing tile, next to the fee it came out of.
        */}
        <StatTile
          label="Subscription refunds"
          value={noRollupYet ? "—" : formatCents(subscriptionRefunded)}
          secondary="Lifetime · Markit's own money, already netted out of subscription revenue"
          tone={!noRollupYet && subscriptionRefunded > 0 ? "warning" : "neutral"}
          freshness={freshness}
        />
        {/*
          Only on the net basis, so nothing about the gross page changes for
          anyone who never flips the switch. It is the figure the net numbers
          are built from, so it belongs beside them rather than only inside
          them — and it is a real cost, unlike the refund tiles next to it.
        */}
        {isNet && (
          <StatTile
            label="Stripe processing fees"
            value={noRollupYet || !hasFee ? "—" : formatCents(lifetimeStripeFee!)}
            secondary={
              noRollupYet
                ? "Not yet recorded"
                : !hasFee
                ? "Never measured for this customer — backfill pending"
                : "Lifetime · Stripe's cut, kept by Stripe even on refunded charges"
            }
            title="Read per charge from balance_transaction.fee. Stripe does not return its fee when a charge is refunded, so this is subtracted in full whatever has since been given back."
            freshness={freshness}
          />
        )}
      </CardGrid>

      <div style={{ marginTop: 16 }}>
        <ChartCard
          title={`Subscription vs ticketing fees, last 12 months${
            isNet ? " (net)" : ""
          }`}
          subtitle={
            isNet
              ? `Nightly Stripe rollup · this creator's monthly totals, net of refunds and Stripe fees · ${monthsWithFee} of ${months.length} months have a fee recorded`
              : "Nightly Stripe rollup · this creator's monthly totals, net of refunds"
          }
          series={SERIES}
          dimmed={refreshing}
          freshness={freshness}
          table={{
            categoryLabel: "Month",
            rows: months.map(row => ({
              category: monthLabel(row.month),
              values: {
                subscription: formatCents(row.subscription),
                ticketing: formatCents(row.ticketing),
                other: formatCents(row.other),
              },
            })),
          }}
        >
          {months.length === 0 ? (
            <EmptyChartNote label="No monthly revenue recorded for this customer yet." />
          ) : (
            <StackedColumns
              data={months}
              series={SERIES}
              categoryKey="month"
              tickFormatter={monthLabel}
              labelFormatter={monthLabel}
              // These series are CENTS — without this the axis read "800k" for
              // $8,000.
              valueTickFormatter={formatCentsCompact}
            />
          )}
        </ChartCard>
      </div>

      <div style={{ marginTop: 16 }}>
        <ChartCard
          title="Subscription detail"
          subtitle="Firestore · users.customer"
          freshness={{ kind: "live" }}
        >
          <FactList
            facts={[
              { label: "Plan", value: subscription.plan },
              {
                label: "Baseline",
                value: `${formatCents(subscription.baselineAmountCents, true)} / month`,
              },
              {
                label: "Current period",
                value: `${formatDate(subscription.periodStart)} → ${formatDate(
                  subscription.periodEnd
                )}`,
              },
              {
                label: "Messages this cycle",
                value: overflowUsed,
                title: "Baseline allowance; anything beyond it bills as overflow",
              },
              {
                label: "Scheduled change",
                value: subscription.downgradeScheduled ? (
                  <TagChip label="Downgrade scheduled" tone="warning" />
                ) : (
                  "None"
                ),
              },
              {
                label: "Cancellation",
                value: subscription.cancelAtEnd ? (
                  <TagChip label="Cancels at period end" tone="warning" />
                ) : (
                  "Not canceling"
                ),
              },
            ]}
          />
        </ChartCard>
      </div>
    </Section>
  );
};

export default RevenueSection;
